<template>
  <div>
    <!-- Header -->
    <div
      class="flex items-center justify-between border-b border-neutral-300 bg-neutral-200 px-6 py-4"
    >
      <h2 class="text-navy-800 flex items-center gap-2 text-sm font-semibold">
        <i class="pi pi-clock text-navy-400" />
        Activity
      </h2>
      <a
        v-if="events.length > 0"
        v-tooltip.top="'Export CSV'"
        :href="`/orders/${orderId}/activity_export`"
        class="text-navy-400 hover:text-navy-700 transition-colors"
      >
        <i class="pi pi-download text-sm" />
      </a>
    </div>

    <!-- Timeline list (scrollable) -->
    <div
      v-if="events.length > 0"
      :class="expanded ? 'activity-scroll-expanded' : 'activity-scroll'"
    >
      <div
        v-for="(event, i) in events"
        :key="event.id"
        class="relative flex gap-3.5 px-6 py-3"
      >
        <!-- Dot + connector line -->
        <div class="relative flex flex-col items-center pt-1.5">
          <div
            :class="[
              'h-2 w-2 shrink-0 rounded-full',
              i === 0
                ? 'bg-blue-500 ring-[3px] ring-blue-100'
                : 'bg-neutral-200',
            ]"
          />
          <div
            v-if="i < events.length - 1"
            class="absolute top-[18px] bottom-[-4px] w-[1.5px] bg-neutral-200"
          />
        </div>

        <!-- Content -->
        <div class="min-w-0 flex-1">
          <p class="text-navy-700 text-[13px] leading-snug">
            <span v-if="event.actorLabel" class="text-navy-800 font-semibold">
              {{ event.actorLabel }}
            </span>
            {{ " " }}
            <span v-if="event.subjectLabel">
              {{ eventVerb(event.eventType) }}
              {{ " " }}
              <span class="text-navy-800 font-semibold">
                {{ event.subjectLabel }}
              </span>
            </span>
            <span v-else>
              {{ eventVerb(event.eventType) }}
            </span>
          </p>
          <p class="text-navy-400 mt-0.5 text-[11.5px]">
            {{ formatTime(event.occurredAt) }}
          </p>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else
      class="flex min-h-[120px] flex-col items-center justify-center p-8 text-center"
    >
      <p class="text-navy-600 text-sm font-medium">No activity yet</p>
      <p class="text-navy-400 mt-1 text-xs">
        Events will appear here as the order progresses
      </p>
    </div>

    <!-- Footer -->
    <div
      v-if="events.length > 0"
      class="border-t border-neutral-100 px-6 py-3 text-center"
    >
      <button
        class="text-navy-500 hover:text-navy-800 inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold transition-colors"
        @click="expanded = !expanded"
      >
        {{ expanded ? "Show less" : "View all activity" }}
        <i
          :class="['pi text-xs', expanded ? 'pi-angle-up' : 'pi-angle-down']"
        />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { format, formatDistanceToNow, isYesterday } from "date-fns"
import { ref } from "vue"

import type { OrderEventData } from "../../pages/Order/types"

defineProps<{
  events: OrderEventData[]
  orderId: string
}>()

const expanded = ref(false)

// ─── Event Display ───────────────────────────────────────────────

const actionVerbs: Record<string, string> = {
  order_created: "created this order",
  order_completed: "marked order as completed",
  order_cancelled: "cancelled this closing",
  document_uploaded: "uploaded",
  document_swapped: "swapped",
  signature_captured: "signed",
  identity_verified: "verified identity of",
  table_connected: "connected to closing table",
  participant_added: "added as participant",
  participant_updated: "updated",
  participant_removed: "removed",
  participants_imported: "imported participants",
  documents_import_started: "started document import",
  documents_imported: "Document import completed",
  documents_push_started: "started sending documents",
  documents_pushed: "Documents sent to source",
  documents_push_failed: "Documents failed to send",
  verification_configured: "enabled identity verification",
  portal_token_issued: "sent document access to",
  portal_pin_reset: "reset PIN and resent access to",
  portal_access_revoked: "revoked portal access for",
  portal_accessed: "accessed the document portal",
  portal_lockout: "was locked out of the document portal",
  portal_document_accessed: "downloaded a document",
  portal_all_documents_accessed: "downloaded all documents",
}

const eventVerb = (eventType: string): string =>
  actionVerbs[eventType] ?? eventType

// ─── Time Formatting ─────────────────────────────────────────────

const formatTime = (iso: string): string => {
  const date = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffHours = diffMs / (1000 * 60 * 60)

  if (diffHours < 24) {
    return formatDistanceToNow(date, { addSuffix: true })
  }

  if (isYesterday(date)) {
    return `Yesterday at ${format(date, "h:mm a")}`
  }

  if (date.getFullYear() === now.getFullYear()) {
    return format(date, "MMM d 'at' h:mm a")
  }

  return format(date, "MMM d, yyyy")
}
</script>

<style scoped>
.activity-scroll {
  max-height: 320px;
  overflow-y: auto;
  padding: 8px 0;
  scrollbar-width: thin;
  scrollbar-color: var(--color-navy-200) transparent;
}
.activity-scroll::-webkit-scrollbar {
  width: 4px;
}
.activity-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.activity-scroll::-webkit-scrollbar-thumb {
  background: var(--color-navy-200);
  border-radius: 4px;
}
.activity-scroll-expanded {
  padding: 8px 0;
}
</style>
